import React, { Fragment, useContext, useEffect, useState } from 'react';
import { AssessmentContext } from '../context/AssessmentContext';
import { CountryContext } from '../context/CountryContext';
import { fetchFirstExistingVisaNode, fetchNextExistingVisaNode, fetchExistingVisaIndicative, saveExistingVisaResult } from '../services/ExistingVisaService';
import EXISTING_VISA_STEP_INDEX from '../consts/EXISTING_VISA_STEP_INDEX';
import EXISTING_VISA_TYPES from '../consts/EXISTING_VISA_TYPES';
import ErrorOutlineOutlined from '@material-ui/icons/ErrorOutlineOutlined';
import Loading from './Loading';
import Select, { components } from 'react-select';
import IconCheckmark from './IconCheckmark';

const ExistingVisaNodes = () => {
  // Context variables
  const [assessmentState, assessmentDispatch] = useContext(AssessmentContext);
  const [countryState] = useContext(CountryContext);

  // State variables
  const [isLoading, setIsLoading] = useState(false);
  const [node, setNode] = useState(null);
  const [previouslyAnsweredNodes, setPreviouslyAnsweredNodes] = useState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const NEW_PERMIT_TITLE = 'New Permit or Transfer of Permit May Be Required';
  const FURTHER_ASSESSMENT_TITLE = 'Further assessment required';

  useEffect(() => {
    fetchNode({ isFirst: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const getResultIcon = (variant) => {
    switch (variant) {
      case 'success':
        return <div className="gw__results-success"><IconCheckmark /></div>;
      case 'warning':
        return <div className="gw__warning-icon-yellow"><i className="icon-error_special" /></div>;
      default:
        return <ErrorOutlineOutlined className="gw__warning-icon" />;
    }
  };

  const dispatchExistingVisaResult = async ({ title, isContinuingToAssessment, iconVariant = 'error', indicativeTime, textHtml, subtext, saveExistingVisaResult: shouldSave }) => {
    const icon = getResultIcon(iconVariant);
    const resolvedSubtext = subtext || (textHtml && <span dangerouslySetInnerHTML={{ __html: textHtml }} />);
    
    if (isContinuingToAssessment) {
      let existingVisaResultPayload = null;
       existingVisaResultPayload = {
        existingVisaId: assessmentState.selectedExistingVisa.value.id,
        existingVisaName: assessmentState.selectedExistingVisa.label,
        existingVisaResult: title,
      };
      assessmentDispatch({
        type: 'SET_EXISTING_VISA_RESULT',
        existingVisaResult: { title, icon, subtext: resolvedSubtext, isContinuingToAssessment, indicativeTime },
        existingVisaResultPayload,
        currentExistingVisaIndex: EXISTING_VISA_STEP_INDEX.result,
      });
    }
    else {
      assessmentDispatch({
        type: 'SET_EXISTING_VISA_RESULT',
        existingVisaResult: { title, icon, subtext: resolvedSubtext, isContinuingToAssessment, indicativeTime },
        currentExistingVisaIndex: EXISTING_VISA_STEP_INDEX.result,
      });
    }
    
    if (shouldSave && !isContinuingToAssessment) {
      const destinationCountry = countryState.selectedDestinationCountry.name;
      const region = countryState.selectedDestinationCountry.regions[0].name;

      const existingVisaPayload = {
        destinationCountry,
        region,
        existingVisa: assessmentState.selectedExistingVisa.value.id,
        // Store the dropdown option the user actually selected leading to the result
        existingVisaName: assessmentState.selectedExistingVisa.label,
        // Result title shown to the user
        existingVisaResult: title,
        assessmentTime: assessmentState.startTime || null,
      };
      try {
        await saveExistingVisaResult(existingVisaPayload);
      } catch (err) {
        console.error('Failed to save existing visa result', err);
      }
    }
  };

  
  
  // Unified node fetcher for first and subsequent nodes
  const fetchNode = async ({ edgeId = null, isFirst = false } = {}) => {
    setIsLoading(true);
    try {
      let response;
      if (isFirst) {
        response = await fetchFirstExistingVisaNode(
          countryState.selectedDestinationCountry.id,
          countryState.selectedDestinationCountry.regions[0].id,
          assessmentState.selectedExistingVisa.value.id
        );
      } else {
        setPreviouslyAnsweredNodes(prev => prev.concat(node));
        response = await fetchNextExistingVisaNode(
          countryState.selectedDestinationCountry.id,
          countryState.selectedDestinationCountry.regions[0].id,
          assessmentState.selectedExistingVisa.value.id,
          edgeId
        );
        setSelectedEdge(null);
      }

      const isArrayNoNodes = Array.isArray(response) && response.length === 0;
      const { type, title, text } = response;

      const fetchIndicativeTime = async () => {
        if (!response || Array.isArray(response) || !response.id) return undefined;

        try {
          const indicativeTimeResponse = await fetchExistingVisaIndicative(
            countryState.selectedDestinationCountry.id,
            countryState.selectedDestinationCountry.regions[0].id,
            response.id
          );
          return indicativeTimeResponse?.indicativeTime;
        } catch (err) {
          console.error('Failed to fetch indicative time', err);
          return undefined;
        }
      };

      switch (true) {
        case isArrayNoNodes:
          dispatchExistingVisaResult({
            title: NEW_PERMIT_TITLE,
            subtext: <span>Based on the information provided a new permit or a transfer of the current permit may be required. Click next to continue the assessment.</span>,
            isContinuingToAssessment: true,
            saveExistingVisaResult: false,
          });
          break;
        case type === EXISTING_VISA_TYPES.endNode && title === 'Further Assessment Required':
          dispatchExistingVisaResult({
            title,
            textHtml: text,
            isContinuingToAssessment: true,
            saveExistingVisaResult: false,
          });
          break;
        case type === EXISTING_VISA_TYPES.endNode:
          {
            const indicativeTime = await fetchIndicativeTime();
            await dispatchExistingVisaResult({
              title,
              textHtml: text,
              isContinuingToAssessment: false,
              indicativeTime,
              saveExistingVisaResult: true,
            });
          }
          break;
        case !isFirst && type === EXISTING_VISA_TYPES.isEligibleNode:
          {
            const indicativeTime = await fetchIndicativeTime();
            await dispatchExistingVisaResult({
              title,
              textHtml: text,
              isContinuingToAssessment: false,
              iconVariant: 'success',
              indicativeTime,
              saveExistingVisaResult: true,
            });
          }
          break;
        case !isFirst && type === EXISTING_VISA_TYPES.continueToAssessmentNode:
          {
            const indicativeTime = await fetchIndicativeTime();
            dispatchExistingVisaResult({
              title: FURTHER_ASSESSMENT_TITLE,
              textHtml: text,
              isContinuingToAssessment: true,
              iconVariant: 'warning',
              indicativeTime,
              saveExistingVisaResult: false,
            });
          }
          break;
        default:
          response.dropdownOptions = dropdownBuilder(response);
          setNode(response);
          return; // early return keeps loading state managed in finally
      }
    } catch (e) {
      console.error('Error fetching existing visa node', e);
      dispatchExistingVisaResult({
        title: 'An error occurred',
        subtext: <span>Unable to load visa information. Please try again.</span>,
        isContinuingToAssessment: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const dropdownBuilder = (Node) => Node.childEdges.map(edge => ({ label: edge.text, value: edge }));

  const fetchNextNode = () => {
    if (!selectedEdge) return;
    fetchNode({ edgeId: selectedEdge.value.id });
  };

  const fetchPreviousNode = () => {
    if (previouslyAnsweredNodes.length === 0) return;
    setIsLoading(true);
    const prevList = previouslyAnsweredNodes.slice(0, -1);
    const previousNode = previouslyAnsweredNodes[previouslyAnsweredNodes.length - 1];
    setPreviouslyAnsweredNodes(prevList);
    setNode(previousNode);
    setIsLoading(false);
  };

  const DropdownIndicator = props => {
    return (
      <components.DropdownIndicator {...props}>
        <i className="icon-simple_arrow_down_special gw__requirement-select-icon" />
      </components.DropdownIndicator>
    );
  };

  return (
    <div className="row">
      {
        isLoading || !node
        ? <Loading />
        : <Fragment>
            <div className="col-12 d-flex justify-content-between align-items-center mb-3">
              <span className="gw__existing-visa-section-title">EXISTING VISA</span>
            </div>

            <div className="col-12">
              <p className="gw__existing-visa-question">
                {node.text}
              </p>
            </div>

            <div className="col-12">
              <p className="gw__existing-visa-subtext">
                {node.subtext}
              </p>
            </div>

            <div className="col-12">
              <Select
                className="gw__requirement-react-select-container"
                classNamePrefix="gw__requirement-react-select"
                components={{DropdownIndicator}}
                options={node.dropdownOptions}
                onChange={event => { setSelectedEdge(event);}}
                value={selectedEdge}
              />
            </div>

            {
              previouslyAnsweredNodes.length > 0
              ? <div className="col-12 d-flex justify-content-between mt-5">
                  <button className="btn btn-secondary" onClick={fetchPreviousNode}>BACK</button>
                  <button className="btn btn-primary" onClick={fetchNextNode} disabled={!selectedEdge}>NEXT</button>
                </div>
              : <div className="col-12 d-flex justify-content-end mt-5">
                  <button className="btn btn-primary" onClick={fetchNextNode} disabled={!selectedEdge}>NEXT</button>
                </div>
            }
          </Fragment>
      }
    </div>
  )
};

export default ExistingVisaNodes;
